import { createSeededNoise } from "./perlinNoise";

export interface TerrainParams {
    canvasW: number;
    canvasH: number;
    sampleStep: number;
    seed: string;
    /** Noise-space scale (units per canvas pixel), same idea as domain warp. */
    noiseScale: number;
    /** Number of FBM octaves. */
    octaves: number;
}

/**
 * Fractional Brownian Motion: sum `octaves` layers of `noiseFn` with doubling
 * frequency and halving amplitude. Output ≈ [0, 1] when `noiseFn` returns [0, 1].
 */
function fbm(
    noiseFn: (x: number, y: number) => number,
    x: number,
    y: number,
    octaves: number,
): number {
    let value = 0;
    let amplitude = 0.5;
    let frequency = 1;
    for (let i = 0; i < octaves; i++) {
        value += amplitude * noiseFn(x * frequency, y * frequency);
        amplitude *= 0.5;
        frequency *= 2;
    }
    return value;
}

/**
 * Un-warped FBM height field for topographic-style contours.
 * Values are in roughly [0, 1]; border is forced to 0 so iso-contours close inside the canvas.
 */
export function computeTerrainField(params: TerrainParams): number[][] {
    const { canvasW, canvasH, sampleStep, seed, noiseScale, octaves } = params;
    const noiseV = createSeededNoise(seed + "_terrain");

    const sampleCols = Math.ceil(canvasW / sampleStep) + 1;
    const sampleRows = Math.ceil(canvasH / sampleStep) + 1;

    const field: number[][] = [];

    for (let r = 0; r < sampleRows; r++) {
        field[r] = new Array<number>(sampleCols).fill(0);
        const sy = r * sampleStep;
        for (let c = 0; c < sampleCols; c++) {
            const sx = c * sampleStep;
            const px = sx * noiseScale;
            const py = sy * noiseScale;
            field[r][c] = fbm(noiseV, px, py, octaves);
        }
    }

    const lastRow = sampleRows - 1;
    const lastCol = sampleCols - 1;
    for (let c = 0; c < sampleCols; c++) {
        field[0][c] = 0;
        field[lastRow][c] = 0;
    }
    for (let r = 0; r < sampleRows; r++) {
        field[r][0] = 0;
        field[r][lastCol] = 0;
    }

    return field;
}

/**
 * Min/max over all samples (including border zeros).
 */
export function computeFieldMinMax(
    field: number[][],
): { minV: number; maxV: number } | null {
    const sampleRows = field.length;
    const sampleCols = field[0]?.length ?? 0;
    if (sampleRows < 2 || sampleCols < 2) return null;

    let minV = Infinity;
    let maxV = -Infinity;
    for (let r = 0; r < sampleRows; r++) {
        for (let c = 0; c < sampleCols; c++) {
            const v = field[r][c];
            if (v < minV) minV = v;
            if (v > maxV) maxV = v;
        }
    }
    if (!isFinite(minV) || !isFinite(maxV) || maxV <= minV) return null;
    return { minV, maxV };
}

/**
 * Evenly spaced thresholds between minV and maxV (exclusive of endpoints via (k+0.5)/n).
 * Stable convention: k = 0 … contourCount-1.
 */
export function computeTopoThresholds(
    minV: number,
    maxV: number,
    contourCount: number,
): number[] {
    if (contourCount < 1 || maxV <= minV) return [];
    const out: number[] = [];
    const span = maxV - minV;
    for (let k = 0; k < contourCount; k++) {
        out.push(minV + ((k + 0.5) / contourCount) * span);
    }
    return out;
}
